import { useCallback, useEffect, useRef, useState } from 'react';
import { useRealtimeFeed } from 'services/client/realtime';
import type {
  FeedClass,
  FlowRow,
  FlowTickerInfo,
  RtSnapshotBody,
  Tier,
} from 'constants/payloads/realtime';
import { appendRows, dedupeSortCap } from './flowRowMerge';

// Cap on client-held smart-feed rows. Must exceed the busiest real smart-feed day
// or a column sort silently omits truncated rows (§17.5). Set to 50k headroom;
// react-window virtualizes so this is a memory bound, not a render cost. Re-verify
// against production before P3 and raise if a real day approaches it.
const MAX_ROWS = 50_000;
const FLUSH_MS = 500; // D23: flush to React state at most every 500ms

export interface OptionFlowRealtime {
  rows: FlowRow[];
  tickers: Record<string, FlowTickerInfo>;
  status: string;
  /** Server-resolved tier. */
  tier: Tier | null;
  /**
   * The ET trading date of the session the server is streaming (YYYY-MM-DD), or null before the
   * first snapshot and while the server has no session. The server starts a new date only after
   * real trades dated that day arrive, so an empty feed on today's date means today's trades exist
   * but none have reached this viewer yet.
   */
  sessionDate: string | null;
}

/**
 * Main option flow realtime state (spec §11.3, Phase 3). Snapshot seeds the
 * session; deltas are buffered and flushed every ≤500ms (D23). Ticker info is
 * merged eagerly (small). session_reset clears everything (day rollover).
 * Returns raw FlowRows — the page applies the client-side side filter then joins
 * ticker info in a single derive pass (FlowPageContainer) before rendering.
 *
 * `feedClass` selects the smart/filtered feed (default) or the `full` baseline
 * firehose (paid-only, opt-in via the command center). Changing it re-subscribes
 * server-side — the DO sends a different row set, not a client-side filter — so the
 * held rows are cleared while the new snapshot loads (baseline rows must not linger
 * when toggling back to smart, and vice versa).
 */
export function useOptionFlowRealtime(
  enabled: boolean,
  feedClass: FeedClass = 'smart',
): OptionFlowRealtime {
  const [rows, setRows] = useState<FlowRow[]>([]);
  const [tickers, setTickers] = useState<Record<string, FlowTickerInfo>>({});
  const [sessionDate, setSessionDate] = useState<string | null>(null);
  const buffer = useRef<FlowRow[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Canonical held rows, mirrored to `rows` state for rendering. The merge reads/writes this
  // ref and `seen` outside any setState updater — updaters must stay pure (React StrictMode
  // double-invokes them in dev), and the incremental merge mutates `seen`, so it runs here.
  const rowsRef = useRef<FlowRow[]>([]);
  // Persistent seq dedupe set, kept in sync with `rowsRef` across flushes so a delta never
  // triggers a full re-scan of the held session. Cleared alongside commit([]).
  const seen = useRef<Set<number>>(new Set());

  const commit = useCallback((next: FlowRow[]) => {
    rowsRef.current = next;
    setRows(next);
  }, []);

  const flush = useCallback(() => {
    timer.current = null;
    if (!buffer.current.length) return;
    const batch = buffer.current;
    buffer.current = [];
    const next = appendRows(rowsRef.current, batch, seen.current, MAX_ROWS);
    if (next !== rowsRef.current) commit(next);
  }, [commit]);

  const schedule = useCallback(() => {
    if (timer.current) return;
    timer.current = setTimeout(flush, FLUSH_MS);
  }, [flush]);

  const mergeTickers = useCallback((incoming?: Record<string, FlowTickerInfo>) => {
    if (!incoming || Object.keys(incoming).length === 0) return;
    setTickers((prev) => ({ ...prev, ...incoming }));
  }, []);

  const clearHeld = useCallback(() => {
    buffer.current = [];
    seen.current.clear();
    commit([]);
    setTickers({});
  }, [commit]);

  const { status, tier } = useRealtimeFeed({
    feed: 'flow',
    enabled,
    params: { feedClass },
    onSnapshot: (body, hello) => {
      const snap = body as RtSnapshotBody;
      // A fresh connect always gets a full snapshot; diff/none resumes keep the date they resumed.
      if (hello) setSessionDate(hello.sessionDate);
      buffer.current = [];
      commit(dedupeSortCap(snap.rows ?? [], seen.current, MAX_ROWS));
      setTickers(snap.tickers ?? {});
    },
    onDelta: (delta) => {
      setSessionDate(delta.sessionDate);
      buffer.current.push(...(delta.rows as FlowRow[]));
      mergeTickers(delta.tickers);
      schedule();
    },
    onReset: (reset) => {
      clearHeld();
      setSessionDate(reset.sessionDate);
    },
  });

  useEffect(() => {
    if (!enabled) {
      clearHeld();
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [enabled, clearHeld]);

  // Toggling feed class re-subscribes; clear held rows so the incoming snapshot fully
  // replaces them (else baseline rows linger when switching back to smart, or the smart
  // set briefly shows as "full" before the baseline snapshot arrives).
  useEffect(() => {
    clearHeld();
  }, [feedClass, clearHeld]);

  // Clear held rows on fallback: otherwise the surface's sticky `usingRealtime` gate keeps
  // rendering rows frozen at the outage start AS live, and the 11-min retry's 'connecting'
  // re-arms that gate — swapping fresh Apollo data for stale rows and tearing down polling
  // every retry. Fallback renders Apollo anyway; a retry-success re-snapshots fresh (§17.12 R4).
  useEffect(() => {
    if (status === 'fallback') {
      clearHeld();
    }
  }, [status, clearHeld]);

  return { rows, tickers, status, tier, sessionDate };
}
